/**
 * 自動化システムの台帳。
 *
 * 既存のコックピット（keiri-task-manager の `app/_lib/keiriSystems.ts`）の定義を、
 * サイドバーに並べた 10 件ぶん写したもの。項目の意味もそちらに揃えている。
 *
 * `runs` は「人が押して動かすもの」だけを持つ。コックピット側で実行ボタンを外して
 * シートのメニューから動かす運用にしたもの（支払スケジュール・残高照合など）は、
 * 実行ボタンを置かずに関連シートへのリンクだけを出す。
 *
 * 実行の繋ぎ込み（GAS の WebApp / GitHub Actions）はタスク管理が動いたあとに行うため、
 * ここには URL やトークンは書かない（環境変数の名前だけを `env` で持つ）。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 自動化システムの台帳。分類・種類・起動のしかた・人の関わり・実行できること・仕組み・関連シートを持つ。
 * @graph-connects none
 */
import type { AutomationId } from '@/components/Sidebar';

/**
 * 月次で人がどう関わるか。
 *
 * - `承認`: 機械が下処理をして、人の承認が必要
 * - `実行`: 人が手で起動する
 * - `なし`: 完全自動で、通知だけ見ればよい
 */
export type AutomationPolicy = '承認' | '実行' | 'なし';

/** 仕組みの図解の 1 工程 */
export interface FlowStep {
  /** 工程名 */
  title: string;
  /** 補足 */
  detail?: string;
  /** 使うツール（freee / GAS / Sheets など） */
  tool?: string;
  /** 実際のプロジェクト名・シート名 */
  resource?: string;
  /** 工程の種類。start=データ源 / proc=処理 / human=人の確認・承認 / out=出力 */
  kind: 'start' | 'proc' | 'human' | 'out';
}

/** 押して動かせること */
export interface AutomationRunAction {
  /** ボタンの文言 */
  label: string;
  /** 押したときに出す確認文 */
  confirm?: string;
  /** 実行前に選ぶもの。`month`=対象月、`period`=開始月と終了月 */
  parameter?: 'month' | 'period';
}

/** 使用ツールの種類 */
export type ToolKind = 'gas' | 'sheet' | 'github' | 'other';

/** 使用ツールへのリンク 1 件 */
export interface ToolLink {
  /** 種類。画面では種類ごとの札を付けて並べる */
  kind: ToolKind;
  /** 表示名 */
  label: string;
  /** 開く URL */
  url: string;
}

/** 自動化システム 1 件 */
export interface Automation {
  /** サイドバーの識別子 */
  id: AutomationId;
  /** サイドバーに出す短い名前 */
  shortName: string;
  /** 台帳の名前 */
  name: string;
  /** 月次経理の大分類に合わせた分類 */
  category: string;
  /** 実装の種類（GAS / Pythonバッチ など） */
  type: string;
  /** どう起動するか */
  trigger: string;
  /** 人の関わり方 */
  policy: AutomationPolicy;
  /** 押して動かせること */
  runs: readonly AutomationRunAction[];
  /** 実行を繋ぐときに使う環境変数のプレフィックス */
  env?: string;
  /** 仕組みの図解 */
  flow: readonly FlowStep[];
  /** 図解の補足 */
  note?: string;
  /** 使用ツールのリンク（GAS・スプレッドシート・GitHub など） */
  links: readonly ToolLink[];
  /** 紐づく月次経理タスク。`大分類|中分類|タスク名|事業` */
  fmtKeys: readonly string[];
}

/** @graph-connects none */
export const AUTOMATION_CATALOG: Record<AutomationId, Automation> = {
  'auto-payment-prep': {
    id: 'auto-payment-prep',
    shortName: '支払準備',
    name: '支払業務（証憑・承認）',
    category: '07.支払関連',
    type: 'GAS',
    trigger: '毎週金＋随時',
    policy: '承認',
    runs: [],
    flow: [
      { title: '証憑PDF', detail: '取引に添付された請求書', tool: 'freee', kind: 'start' },
      {
        title: '証憑同期',
        detail: 'Drive へ自動保存・改訂検知',
        tool: 'GAS',
        resource: '支払業務自動化',
        kind: 'proc',
      },
      {
        title: '支払管理レポート作成',
        detail: '未払取引を 14 列に整形',
        tool: 'GAS',
        resource: 'YYYYMMDD_支払管理レポート',
        kind: 'proc',
      },
      { title: '内容確認・未着チェック', detail: 'スプシで目視', kind: 'human' },
      { title: '証憑整理', detail: '15日／25日フォルダへ', tool: 'Drive', kind: 'proc' },
      { title: '未着リマインド', detail: '一覧を確認してから送信', tool: 'Slack', kind: 'proc' },
      { title: '全銀DL→口座照合', detail: '振込前チェック', tool: 'コックピット', kind: 'out' },
    ],
    links: [],
    fmtKeys: [
      '07.支払関連|ー|15日支払準備（スタイリスト報酬・経費）|共通',
      '07.支払関連|ー|25日支払準備（freeeの支払管理レポートからスプシ作成）|共通',
      '07.支払関連|ー|25日支払準備（データ格納）|共通',
      '07.支払関連|ー|invoiceに来ている請求書の差し替え（1回目）|共通',
      '07.支払関連|ー|invoiceに来ている請求書の差し替え（2回目）|共通',
      '07.支払関連|支払い管理レポートチェック|支払管理レポートのチェック（15日）|共通',
      '07.支払関連|支払い管理レポートチェック|支払管理レポートのチェック（25日）|共通',
      '07.支払関連|支払い管理レポートチェック|支払管理レポートのチェック（月初）|共通',
    ],
  },
  'auto-invoice-check': {
    id: 'auto-invoice-check',
    shortName: 'AI請求書チェック',
    name: 'AI請求書チェック（振込前チェック）',
    category: '07.支払関連',
    type: 'Pythonバッチ（Cloud Run）',
    trigger: '支払日ごとに手動',
    policy: '承認',
    runs: [],
    flow: [
      { title: '全銀データ', detail: 'freee の一括振込から出力', tool: 'freee', kind: 'start' },
      { title: '請求書PDF・画像', detail: '支払フォルダの確定版', tool: 'Drive', kind: 'start' },
      {
        title: '請求書の読み取り',
        detail: '口座・金額・期日・請求書番号を抽出',
        tool: 'Gemini',
        resource: '取引先別ルール（読み方の指示表）',
        kind: 'proc',
      },
      {
        title: '照合',
        detail: '口座→振込先名→金額の順で請求書を特定し、金額・名義・期日・漏れ・重複を判定',
        tool: 'Python',
        kind: 'proc',
      },
      {
        title: '要確認の請求書を開いて確認',
        detail: '要確認・異常の明細だけ人が見る',
        kind: 'human',
      },
      { title: '振込実行', detail: '全銀を銀行へ', kind: 'out' },
    ],
    note: 'cortex へ移行中。移行が終わるまでは SFT Hub の「振込前チェック」を使う。',
    links: [
      {
        kind: 'sheet',
        label: '請求書読み取りルール',
        url: 'https://docs.google.com/spreadsheets/d/19toOU5AMFNXdVkCaG8u4E_SAKgQkGPJZBgTExpkwD2o',
      },
      {
        kind: 'github',
        label: 'keiri-payment（移行元）',
        url: 'https://github.com/myamazaki-ui/keiri-payment',
      },
    ],
    fmtKeys: [],
  },
  'auto-expense-check': {
    id: 'auto-expense-check',
    shortName: '経費チェック',
    name: '経費チェック',
    category: '05.経費関連',
    type: 'Cloud関数／Pythonバッチ',
    trigger: '毎日2回',
    policy: 'なし',
    runs: [
      {
        label: '内容確認（dry-run）',
        confirm: '申請を読むだけで、freee は書き換えません。',
      },
      { label: '承認リマインド' },
      {
        label: '本番実行',
        confirm: 'freee の経費申請を規程と突き合わせ、承認／差戻しまで行います。実行しますか？',
      },
    ],
    flow: [
      { title: '経費申請', detail: '毎日2回取得', tool: 'freee', kind: 'start' },
      {
        title: 'ルールチェック',
        detail: '証憑・金額・科目',
        tool: 'Python',
        resource: '経費チェック自動化（Cloud Run）',
        kind: 'proc',
      },
      { title: '運賃照合', detail: '交通費の経路運賃', tool: '駅すぱあと', kind: 'proc' },
      { title: '承認／差戻し', detail: '本番モードのみ', tool: 'freee', kind: 'proc' },
      { title: '結果通知', tool: 'Slack', kind: 'out' },
    ],
    links: [
      {
        kind: 'github',
        label: 'keiri-expense-checker',
        url: 'https://github.com/myamazaki-ui/keiri-expense-checker',
      },
    ],
    fmtKeys: ['05.経費関連|ー|承認・取引登録|共通'],
  },
  'auto-amount-recon': {
    id: 'auto-amount-recon',
    shortName: '金額突合',
    name: 'お金回りシートと支払依頼の金額の突合',
    category: '41.レンタル用資産',
    type: 'GAS',
    trigger: '手動（〜12日）',
    policy: '実行',
    env: 'RENTAL_SHISAN',
    runs: [
      {
        label: '突合を実行',
        parameter: 'month',
        confirm:
          'お金回りシートと支払依頼の金額の突合を実行し、出力先スプレッドシートを上書きします。実行しますか？（2〜5分かかります）',
      },
    ],
    flow: [
      { title: '支払依頼データ', detail: '勘定科目別に取得', tool: 'freee', kind: 'start' },
      { title: 'お金回りシート読込', tool: 'Sheets', resource: 'NEW請求書シート', kind: 'proc' },
      {
        title: '金額を突合',
        detail: '一致／不一致／freeeのみ／シートのみ',
        tool: 'GAS',
        kind: 'proc',
      },
      { title: '結果を出力', detail: '事業別タブ＋サマリ', tool: 'Sheets', kind: 'out' },
    ],
    links: [
      {
        kind: 'github',
        label: 'keiri-rental-asset-recon',
        url: 'https://github.com/myamazaki-ui/keiri-rental-asset-recon',
      },
    ],
    fmtKeys: ['41.レンタル用資産|ー|お金回りシートと支払依頼の金額の突合|共通'],
  },
  'auto-payroll-csv': {
    id: 'auto-payroll-csv',
    shortName: '給与CSV出力',
    name: 'CSV出力（給与仕訳）',
    category: '12.人件費',
    type: 'GAS',
    trigger: '手動（13日）',
    policy: '実行',
    env: 'KYUYO_CSV',
    runs: [
      { label: '非正規 対象月を更新', parameter: 'month' },
      { label: '正規 対象月を更新', parameter: 'month' },
      { label: '非正規CSV' },
      { label: '正規CSV' },
    ],
    flow: [
      { title: '給与シート', detail: '非正規／正規', tool: 'Sheets', kind: 'start' },
      { title: '金額0行を除外', detail: 'O列フィルタ', tool: 'GAS', kind: 'proc' },
      { title: '仕訳CSVを生成', detail: '非正規3本／正規1本', tool: 'GAS', kind: 'proc' },
      { title: 'CSVをダウンロード', detail: 'ブラウザの保存先へ', tool: 'GAS', kind: 'out' },
    ],
    note: 'Drive には保存されず、ブラウザのダウンロードフォルダに入ります。',
    links: [
      {
        kind: 'github',
        label: 'keiri-kyuyo-csv',
        url: 'https://github.com/myamazaki-ui/keiri-kyuyo-csv',
      },
      {
        kind: 'sheet',
        label: '非正規シート',
        url: 'https://docs.google.com/spreadsheets/d/11jCZ0usaRiooIENZBUGncnnbCgRYXb6Z7L92sVzKkOs/edit?gid=267021324#gid=267021324',
      },
      {
        kind: 'sheet',
        label: '正規シート',
        url: 'https://docs.google.com/spreadsheets/d/11jCZ0usaRiooIENZBUGncnnbCgRYXb6Z7L92sVzKkOs/edit?gid=2077579042#gid=2077579042',
      },
    ],
    fmtKeys: [
      '12.人件費|15日払給与|非正規社員雑給計上（15日払い）|共通',
      '12.人件費|25日払給与|正社員給与計上（非正規25日払いを含む）|共通',
    ],
  },
  'auto-payment-schedule': {
    id: 'auto-payment-schedule',
    shortName: '支払スケジュール',
    name: 'popot 支払スケジュール共有',
    category: '07.支払関連',
    type: 'GAS',
    trigger: '毎月（手動／自動）',
    policy: '実行',
    runs: [],
    flow: [
      { title: '支払予定', detail: '支払管理レポートから', tool: 'Sheets', kind: 'start' },
      { title: 'スケジュール整形', tool: 'GAS', resource: 'popot支払スケジュール', kind: 'proc' },
      { title: 'Slack共有・カレンダー招待', tool: 'Slack／カレンダー', kind: 'out' },
    ],
    note: '実行はシート側のメニューから行う運用です（コックピットでも実行ボタンは置いていません）。',
    links: [
      {
        kind: 'github',
        label: 'keiri-popot-schedule',
        url: 'https://github.com/myamazaki-ui/keiri-popot-schedule',
      },
      {
        kind: 'sheet',
        label: 'P-002_支払スケジュール',
        url: 'https://docs.google.com/spreadsheets/d/1iF-UbrimvTDdTtrD7oONYu5q4QIqYPROHBm56aT6GB4/edit',
      },
    ],
    fmtKeys: ['07.支払関連|ー|次月支払スケジュール決め・共有|共通'],
  },
  'auto-subsidy-pl': {
    id: 'auto-subsidy-pl',
    shortName: '補助金PL共有',
    name: '補助金 月次PL共有（メンズ）',
    category: '91.PL共有',
    type: 'GAS',
    trigger: '手動（〜12日）',
    policy: '実行',
    env: 'HOJOKIN',
    runs: [
      {
        label: '月次推移を更新',
        confirm:
          'freee からメンズ事業の月次PLを取得し、「月次推移」シートを上書きします。実行しますか？（2〜4分かかります）',
      },
    ],
    flow: [
      { title: 'メンズ事業PL', detail: '会計年度12ヶ月分', tool: 'freee', kind: 'start' },
      { title: '科目×取引先×月に整形', tool: 'GAS', kind: 'proc' },
      {
        title: '月次推移シートを更新',
        tool: 'Sheets',
        resource: 'サーキュラーエコノミー補助金_実績管理',
        kind: 'out',
      },
    ],
    note: 'サーキュラーエコノミー補助金の実績管理用です。',
    links: [
      {
        kind: 'github',
        label: 'keiri-hojokin-pl',
        url: 'https://github.com/myamazaki-ui/keiri-hojokin-pl',
      },
    ],
    fmtKeys: [],
  },
  'auto-tanshin': {
    id: 'auto-tanshin',
    shortName: '短信作成',
    name: '短信 自動作成',
    category: '99.四半期',
    type: 'GAS',
    trigger: '四半期・手動',
    policy: '実行',
    runs: [],
    flow: [
      { title: 'PL／BS月次推移', detail: '当期＋前期・四半期', tool: 'freee', kind: 'start' },
      { title: 'シートへ生成', tool: 'GAS', resource: '短信自動作成', kind: 'proc' },
      {
        title: 'テンプレxlsxへ差込',
        detail: 'zip展開→XML差替',
        tool: 'GAS',
        resource: '短信PL_FMT.xlsx／短信BS_FMT.xlsx',
        kind: 'proc',
      },
      { title: '短信用xlsxをダウンロード', tool: 'Drive', kind: 'out' },
    ],
    note: '実行はシートのメニューから行っています（四半期ごと）。',
    links: [
      {
        kind: 'github',
        label: 'keiri-tanshin',
        url: 'https://github.com/myamazaki-ui/keiri-tanshin',
      },
      {
        kind: 'sheet',
        label: '短信 基礎データシート',
        url: 'https://docs.google.com/spreadsheets/d/1Tx1Qgd-FxrkA_XeqC_kG4idLNzOcwRL_fPXK8Gf6_yY/edit',
      },
    ],
    fmtKeys: [],
  },
  'auto-balance-recon': {
    id: 'auto-balance-recon',
    shortName: '残高照合',
    name: '銀行残高照合（通帳×freee×月次推移）',
    category: '42.その他の確認',
    type: 'GAS',
    trigger: '手動（月次）',
    policy: '実行',
    runs: [],
    flow: [
      { title: '通帳記帳の結果', tool: '通帳', kind: 'start' },
      { title: 'freee 残高・月次推移', tool: 'freee', kind: 'start' },
      { title: '残高を突合', tool: 'GAS', resource: '銀行残高照合', kind: 'proc' },
      { title: '差異を確認', kind: 'human' },
      { title: '照合結果シート', tool: 'Sheets', kind: 'out' },
    ],
    note: '実行はシート側のメニュー（freee連携）から。受け口の WebApp は残してあります。',
    links: [
      {
        kind: 'github',
        label: 'keiri-bank-recon',
        url: 'https://github.com/myamazaki-ui/keiri-bank-recon',
      },
      {
        kind: 'sheet',
        label: '銀行残高照合シート',
        url: 'https://docs.google.com/spreadsheets/d/1bmzuzSRUyiXPty4sqCEhaLzzQyupFVDYN1G3qBWbf4s/edit?gid=1121593525#gid=1121593525',
      },
    ],
    fmtKeys: ['01.預金関係|ー|freee残高と月末預金残高との一致確認|共通'],
  },
  'auto-depreciation': {
    id: 'auto-depreciation',
    shortName: '減価償却再計算',
    name: '減価償却再計算（メンズ・レンタル用資産）',
    category: '41.レンタル用資産',
    type: 'Pythonバッチ',
    trigger: '四半期・手動',
    policy: '実行',
    runs: [],
    flow: [
      { title: '個品マスタ', detail: '下代・入庫日', tool: 'DB', kind: 'start' },
      { title: '償却費を再計算', tool: 'Python', kind: 'proc' },
      { title: 'サマリ・明細を出力', tool: 'Sheets', kind: 'out' },
    ],
    links: [
      {
        kind: 'github',
        label: 'keiri-genka-shokyaku',
        url: 'https://github.com/myamazaki-ui/keiri-genka-shokyaku',
      },
      {
        kind: 'sheet',
        label: '結果シート（サマリ・明細・翌期★01）',
        url: 'https://docs.google.com/spreadsheets/d/1w_-V9DCYQ75DJ13UFj_kushVjp0IdznEZEorPG0PO40/edit',
      },
      {
        kind: 'sheet',
        label: '抽出データシート（DB生データ）',
        url: 'https://docs.google.com/spreadsheets/d/1lLfRWuTC6QAnlwq3vTk79PO6iWZrYM4AZtNH2I-qam4/edit',
      },
    ],
    fmtKeys: ['13.レンタル用資産償却費|ー|計算・計上|aC'],
  },
  'auto-sales-recalc': {
    id: 'auto-sales-recalc',
    shortName: '売上再計算',
    name: '売上再計算（DB再集計×freee突合）',
    category: '11.売上関連',
    type: 'GAS',
    trigger: '手動（月次・四半期末）',
    policy: '実行',
    env: 'SALES_RECALC',
    runs: [
      {
        label: 'DB集計→突合を実行',
        parameter: 'period',
        confirm:
          '①DB集計を DB の現在の状態で最新化 → ②対象期間の突合（売上再計算_マスタを更新）→ ③「100_売上再計算／売上再計算_日時出力」フォルダにマスタのコピーを保存します。締まった1四半期（直近の確定四半期）を選んだときは、④その時点の売上明細も Excel で格納します。基幹システムの再計算バッチとは別物なので、四半期末の正式な突合はそれが回った後に実行してください。',
      },
      {
        label: 'freeeキャッシュをクリア',
        confirm:
          'freee 集計キャッシュを全部消します。次回の突合は全期間を取り直すため時間がかかります。実行しますか？',
      },
    ],
    flow: [
      { title: 'サービスDB', detail: '四半期の売上を再集計', tool: 'DB', kind: 'start' },
      { title: 'freee 売上', detail: '期間で取得', tool: 'freee', kind: 'start' },
      { title: '突合して差額を出す', tool: 'GAS', resource: 'keiri-sales-recalc', kind: 'proc' },
      { title: '差額を確認', kind: 'human' },
      { title: 'マスタを更新', tool: 'Sheets', resource: '売上再計算_マスタ', kind: 'out' },
      {
        title: '実行フォルダに保存',
        detail: 'マスタのコピー＋（確定四半期なら）売上明細Excel',
        tool: 'Drive',
        resource: '100_売上再計算',
        kind: 'out',
      },
    ],
    links: [
      {
        kind: 'gas',
        label: 'GAS エディタ',
        url: 'https://script.google.com/d/1Yhla_fVJteoIF4C-fdL81IQzj7F5PCWE1zuC3b4GsEU_28ZGUD5V8Uyn/edit',
      },
      {
        kind: 'github',
        label: 'keiri-sales-recalc',
        url: 'https://github.com/myamazaki-ui/keiri-sales-recalc',
      },
      {
        kind: 'sheet',
        label: '売上再計算_マスタ（最新の突合レポート）',
        url: 'https://docs.google.com/spreadsheets/d/1iXN_WE6UVRdj7XcTwkvZtqJ5QXPFKbcFIn7PRTybFNM/edit',
      },
      {
        kind: 'other',
        label: '実行ごとの出力フォルダ（100_売上再計算）',
        url: 'https://drive.google.com/drive/folders/1br0MBjbQIvnUeZwf68bP6xacOrCtJK-d',
      },
    ],
    fmtKeys: ['99.四半期|ー|売上再計算|mall'],
  },
};
